import { Button } from '@/kit/Button';
import type { LegacyAccountCodeRow } from '@/core/models/firm-settings.models';

/** The panel lists this many mappings; the rest are reached by narrowing the search. */
export const LEGACY_PANEL_LIMIT = 10;

interface Props {
  rows: LegacyAccountCodeRow[];
  /** Filters the grid to the current account: the grid's search is set to its code. */
  onShowCurrent: (code: string) => void;
}

/**
 * BUG-011: "Old Capium code mapping". The chart keeps the current code scheme, so an old Capium code (5440 Accrued
 * Expenses, 8000 Profit & Loss Account b/fwd, 1220 Closing Stock - Raw Materials ...) is not an account of this grid. When the
 * search matches one, this read-only panel under the grid says which current account it was replaced by (the same
 * translation a trial balance import applies). Nothing here can be edited, archived or deleted.
 */
export function LegacyCodeMappingPanel({ rows, onShowCurrent }: Props) {
  if (rows.length === 0) return null;
  const shown = rows.slice(0, LEGACY_PANEL_LIMIT);
  return (
    <section className="legacy-code-mapping" aria-label="Old Capium code mapping">
      <h3 className="legacy-code-mapping-title">Old Capium code mapping</h3>
      <p className="legacy-code-mapping-hint">
        These are codes of the old Capium chart, not accounts of this chart. Each is replaced by the current account
        shown, and a trial balance using the old code is moved to it on import.
      </p>
      <ul className="legacy-code-mapping-list">
        {shown.map((row) => (
          <li key={`${row.oldCode}|${row.newCode}`} className="legacy-code-mapping-row">
            <span className="legacy-code-mapping-text">
              <span className="legacy-code-mapping-old">
                {row.oldCode} {row.oldName} (old code)
              </span>
              {' → '}
              <span className="legacy-code-mapping-new">
                {row.newCode} {row.newName ?? 'not in this chart'} (current)
              </span>
              {row.newCategory && <span className="legacy-code-mapping-category"> · {row.newCategory}</span>}
            </span>
            <Button
              className="legacy-code-mapping-action"
              title={`Show account ${row.newCode} in the grid`}
              onClick={() => onShowCurrent(row.newCode)}
            >
              Show {row.newCode}
            </Button>
          </li>
        ))}
      </ul>
      {rows.length > shown.length && (
        <p className="legacy-code-mapping-more">
          {rows.length - shown.length} more old codes match; type more of the code or name to narrow the list.
        </p>
      )}
    </section>
  );
}
